import { ProjectRow } from "@/components/Projects/Table/ProjectRow"
import { GenericTableClass } from "./GenericTableClass"
import { ProjectTable, TaskTable, AssignmentTable, SubmittalTable, RFITable, InvoiceTable, EmployeeTable, ClientTable, PointOfContactTable, ClientProjectTable, FinanceProjectTable, BidTable, MiniProjectTable } from "./ModelTables"
import { ProjectProps } from "@/interfaces/project_types"
import { TaskProps } from "@/interfaces/tasks_types"
import { TaskRow } from "@/components/Tasks/Table/TaskRow"
import { SubmittalRow } from "@/components/Submittals/Table/SubmittalRow"
import { SubmittalProps } from "@/interfaces/submittal_types"
import { RFIProps } from "@/interfaces/rfi_types"
import { RFIRow } from "@/components/RFIs/Table/RFIRow"
import { AssignmentRow } from "@/components/Assignments/Table/AssignmentRow"
import { AssignmentProps } from "@/interfaces/assignment_types"
import { InvoiceRow } from "@/components/Invoice/Table/InvoiceRow"
import { InvoiceProps } from "@/interfaces/invoices_types"
import { EmployeeProps } from "@/interfaces/employee_type"
import { EmployeeRow } from "@/components/Employee/Table/EmployeeRow"
import { ClientProps } from "@/interfaces/client_types"
import { ClientRow } from "@/components/Clients/Table/ClientRow"
import { PointOfContactProps } from "@/interfaces/point-of-contact_types"
import { PointOfContactRow } from "@/components/PointOfContacts/Table/PointOfContactRow"
import { BidRow } from "@/components/Bids/Table/BidRow"
import { BidProps } from "@/interfaces/bid_types"
import { MiniProjectRow } from "@/components/Projects/Table/ProjectRow"

/**
 * ### Factory class for creating tables
 * 
 * A factory class that creates specialized table instances for different data models.
 * Each create method returns a GenericTableClass configured with:
 * - The appropriate expandable row component for that model
 * - The model name for API requests
 * - Show/hide arrow settings for expandable rows
 * 
 * The table data and columns are defined in the Options folder, including:
 * - Column definitions and sorting
 * - Filter configurations
 * - Default filter states
 * - Table styling and layout
 * 
 * Available table types:
 * - Project tables (full, mini, client, finance views)
 * - Task tables
 * - Submittal tables  
 * - RFI tables
 * - Assignment tables
 * - Invoice tables
 * - Employee tables
 * - Client tables
 * - Point of Contact tables
 * - Bid tables
 */
export class GenericTableFactory {

    static createBidTable(): GenericTableClass<BidProps> {
        return new BidTable({
            expandableRowComponent: (props) => <BidRow data={props.data}/>,
            model_name: "bid",
        })
    }

    static createProjectTable(): GenericTableClass<ProjectProps> {
        return new ProjectTable({
            expandableRowComponent: (props) => <ProjectRow data={props.data}/>,
            model_name: "project",
            show_arrow: true,
        })
    }

    static createMiniProjectTable(): GenericTableClass<ProjectProps> {
        return new MiniProjectTable({
            expandableRowComponent: (props) => <MiniProjectRow data={props.data}/>,
            model_name: "project",
            show_arrow: true,
        })
    }

    static createClientProjectTable(): GenericTableClass<ProjectProps> {
        return new ClientProjectTable({
            expandableRowComponent: (props) => <ProjectRow data={props.data}/>,
            model_name: "project",
            show_arrow: false,
        })
    }

    static createFinanceProjectTable(): GenericTableClass<ProjectProps> {
        return new FinanceProjectTable({
            expandableRowComponent: (props) => <ProjectRow data={props.data}/>,
            model_name: "project",
            show_arrow: false,
        })
    }

    static createTaskTable(): GenericTableClass<TaskProps> {
        return new TaskTable({
            expandableRowComponent: (props) => <TaskRow data={props.data}/>,
            model_name: "task",
        })
    }
    
    static createSubmittalTable(): GenericTableClass<SubmittalProps> {
        return new SubmittalTable({
            expandableRowComponent: (props) => <SubmittalRow data={props.data}/>,
            model_name: "submittal",
        })
    }
    
    static createRFITable(): GenericTableClass<RFIProps> {
        return new RFITable({
            expandableRowComponent: (props) => <RFIRow data={props.data}/>,
            model_name: "rfi",
        })
    }
    
    static createAssignmentTable(): GenericTableClass<AssignmentProps> {
        return new AssignmentTable({
            expandableRowComponent: (props) => <AssignmentRow data={props.data}/>,
            model_name: "assignment",
        })
    }

    static createInvoiceTable(): GenericTableClass<InvoiceProps> {
        return new InvoiceTable({
            expandableRowComponent: (props) => <InvoiceRow data={props.data}/>,
            model_name: "invoice",
        })
    }

    static createEmployeeTable(): GenericTableClass<EmployeeProps> {
        return new EmployeeTable({
            expandableRowComponent: (props) => <EmployeeRow data={props.data}/>,
            model_name: "user",
        })
    }

    static createClientTable(): GenericTableClass<ClientProps> {
        return new ClientTable({
            expandableRowComponent: (props) => <ClientRow data={props.data}/>,
            model_name: "client",
        })
    }

    static createPointOfContactTable(): GenericTableClass<PointOfContactProps> {
        return new PointOfContactTable({
            expandableRowComponent: (props) => <PointOfContactRow data={props.data}/>,
            model_name: "pointofcontact",
        })
    }
    
}